/**
 * Undo Manager — Coordinates undo behavior, toast display, and undo execution.
 *
 * Standard strategy: actions are reflected in the UI immediately and committed to
 * the server immediately. Undo is a compensating action (restore, move back,
 * unarchive, etc.) rather than a delayed commit/cancel timer.
 *
 * Both helpers are stateless: no undo stack, each call is independent.
 * The caller provides all callbacks (optimistic updates, server calls, rollback).
 *
 * @see plans/undo-action-architecture.md for design rationale
 */

import { toast } from 'svelte-sonner';

/** Options for a generic undoable immediate-commit action. */
export interface UndoableActionOptions {
  /** Toast message, e.g. 'Project moved' */
  message: string;
  /** Update UI immediately for the forward action */
  onOptimisticApply?: () => void;
  /** Commit the forward action to the server immediately */
  onCommit: () => Promise<void> | void;
  /** Update UI immediately when Undo is clicked */
  onOptimisticUndo?: () => void;
  /** Commit the compensating undo action to the server */
  onUndo: () => Promise<void> | void;
  /** Roll back UI if the forward commit fails */
  onCommitError?: () => void;
  /** Roll UI forward again if the undo commit fails */
  onUndoError?: () => void;
  /** Toast duration in ms (default: 7000) */
  duration?: number;
  /** Error toast when the forward commit fails */
  commitErrorMessage?: string;
  /** Error toast when the undo commit fails */
  undoErrorMessage?: string;
}

/** Options for undoable soft-delete (destructive actions). */
export interface SoftDeleteOptions {
  /** Toast message, e.g. 'Task deleted' */
  message: string;
  /** API call to soft-delete the resource */
  onDelete: () => Promise<void>;
  /** API call to restore (undiscard) the resource */
  onRestore: () => Promise<void>;
  /** Update UI immediately: remove from list */
  onOptimisticRemove: () => void;
  /** Update UI on undo: add back to list */
  onOptimisticRestore: () => void;
  /** Toast duration in ms (default: 7000) */
  duration?: number;
}

/**
 * Immediate-commit action with undo toast.
 *
 * Flow:
 * 1. Apply to UI immediately (onOptimisticApply)
 * 2. Send forward action to server immediately (onCommit)
 * 3. Show toast with Undo button
 * 4. If undo clicked: update UI immediately (onOptimisticUndo) + call undo endpoint (onUndo)
 * 5. If forward commit fails: rollback UI + show error toast
 * 6. If undo commit fails: roll UI forward again + show error toast
 */
function callMaybeAsync(fn: () => Promise<void> | void): Promise<void> {
  try {
    return Promise.resolve(fn());
  } catch (error) {
    return Promise.reject(error);
  }
}

export function undoableAction(opts: UndoableActionOptions): void {
  const duration = opts.duration ?? 7000;

  opts.onOptimisticApply?.();

  // A compensating request must not overtake its forward request. In particular,
  // restore-before-discard makes the restore a no-op and leaves a just-undone
  // soft-delete discarded once the slower DELETE eventually completes.
  let commitFailed = false;
  const commit = callMaybeAsync(opts.onCommit).catch(() => {
    commitFailed = true;
    opts.onCommitError?.();
    toast.error(opts.commitErrorMessage ?? 'Action failed');
    return false;
  });

  toast(opts.message, {
    duration,
    action: {
      label: 'Undo',
      onClick: () => {
        // If the forward request already failed, its rollback is authoritative.
        if (commitFailed) return;
        opts.onOptimisticUndo?.();
        return commit.then((committed) => {
          if (committed === false) return;
          return callMaybeAsync(opts.onUndo).catch(() => {
            opts.onUndoError?.();
            toast.error(opts.undoErrorMessage ?? 'Failed to undo');
          });
        });
      },
    },
  });
}

/**
 * Soft-delete with undo toast.
 *
 * This is a semantic wrapper around undoableAction for discard/undiscard flows.
 */
export function undoableSoftDelete(opts: SoftDeleteOptions): void {
  undoableAction({
    message: opts.message,
    duration: opts.duration,
    onOptimisticApply: opts.onOptimisticRemove,
    onCommit: opts.onDelete,
    onOptimisticUndo: opts.onOptimisticRestore,
    onUndo: opts.onRestore,
    onCommitError: opts.onOptimisticRestore,
    onUndoError: opts.onOptimisticRemove,
    commitErrorMessage: 'Failed to delete',
    undoErrorMessage: 'Failed to restore',
  });
}
